import React, { useEffect, useRef, useState } from "react"
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Popover,
  Rating,
  TextField,
  Typography,
} from "@mui/material"
import styles from "./Group.module.css"
import { useNavigate } from "react-router-dom"
import { useApiService } from "../hooks/Api/useApiService"
import { Message as MessageIcon } from "@mui/icons-material"
import GroupChatModal from "../Modals/GroupChatModal/GroupChatModal"
import Icn from "../UI/SharieIcn/SharieIcn"
import CaptureVideoImage from "../hooks/Utils/CaptureVideoImage"
import T from "../hooks/Language/T"
import useTranslationSharie from "../hooks/Language/useTranslation"
import UserSection from "../Marketplace/UserSection/UserSection"
import SkillLevelSelect from "../UI/SkillLevelSelect"

const Group = ({
  name,
  cost,
  location,
  duration,
  description,
  max_participants,
  user_name,
  participants,
  image,
  status,
  rating,
  editing,
  setEditedGroup,
  button,
  level,
}) => {
  const [capture_image, setImage] = useState(image)
  const [anchorEl, setAnchorEl] = useState(null)
  const [participants_arr, setParticipantsArr] = useState([])
  const [user, setUser] = useState(null)
  const navigate = useNavigate()
  const { postData } = useApiService()
  const chatRef = useRef()
  const openModal = (modalRef) => modalRef.current?.childFunction()
  const t = useTranslationSharie()
  const [groupData, setGroupData] = useState({
    name,
    cost,
    location,
    duration,
    description,
    max_participants,
    user_name,
    image,
    rating,
    level,
  })

  useEffect(() => {
    postData("users/getuser", { user_name: user_name }).then((data) => {
      setUser(data?.user[0])
    })

    postData("groups/getgroupparticipants", { group_name: name, user_name: user_name }).then(
      (data) => {
        setParticipantsArr(data?.participants)
      }
    )
  }, [participants, user_name])

  const handleOpen = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)
  const handleProfileRedirect = (user_name) => {
    handleClose() // Close the popover
    navigate("/profile", {
      state: { user_name: user_name, editable: false },
    })
  }
  const isVideo = () => {
    const mimeType = image?.split(";")[0] // Get the MIME type
    return mimeType?.startsWith("data:video/")
  }
  const handleOnChange = (field, value) => {
    setGroupData((prev) => ({
      ...prev,
      [field]: value,
    }))
    setEditedGroup(groupData)
  }
  const handleSetImage = (image) => {
    setImage(image)
    setGroupData((prev) => ({
      ...prev,
      image: capture_image,
    }))

    setEditedGroup(groupData)
  }

  return (
    <T>
      <Card style={{ borderRadius: "0.8rem" }}>
        <GroupChatModal ref={chatRef} group={{ name: name, user_name: user_name }} />
        <CardContent className={styles.container}>
          <div>
            {!editing ? (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <Typography variant='h6'>{name}</Typography>
                <UserSection user={user} />
              </div>
            ) : (
              <>
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                  <TextField
                    label={t("Group Name")}
                    variant='outlined'
                    onChange={(e) => handleOnChange("name", e.target.value)}
                    style={{ margin: "1rem" }}
                    defaultValue={name}
                    value={groupData.name} // Ensure the input reflects the current state
                  />
                </div>
                <UserSection user={user} />
              </>
            )}
            {!editing ? (
              <>
                <Grid container spacing={1} p={"1rem"}>
                  <Grid item xs={4}>
                    <Typography variant='body2' fontWeight='bold'>
                      {t("Cost Per User")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant='body2'>{cost}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant='body2' fontWeight='bold'>
                      {t("Location")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant='body2'>{location}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant='body2' fontWeight='bold'>
                      {t("Duration")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant='body2'>{duration}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant='body2' fontWeight='bold'>
                      {t("Description")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography variant='body2'>{description}</Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <Typography variant='body2' fontWeight='bold'>
                      {t("Status")}:
                    </Typography>
                  </Grid>
                  <Grid item xs={8}>
                    <Typography
                      variant='body2'
                      sx={{
                        color: status === "closed" ? "error.main" : "success.main",
                      }}
                    >
                      {status}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  label={t("Cost Per User")}
                  variant='outlined'
                  onChange={(e) => handleOnChange("cost", e.target.value)}
                  style={{ margin: "1rem" }}
                  defaultValue={cost}
                  value={groupData.cost} // Ensure the input reflects the current state
                />
                <SkillLevelSelect
                  value={groupData.level}
                  onChange={(e) => handleOnChange("level", e.target.value)}
                />
                <TextField
                  label={t("Location")}
                  variant='outlined'
                  onChange={(e) => handleOnChange("location", e.target.value)}
                  style={{ margin: "1rem" }}
                  defaultValue={location}
                  value={groupData.location} // Ensure the input reflects the current state
                />
                <TextField
                  label={t("Duration")}
                  variant='outlined'
                  onChange={(e) => handleOnChange("duration", e.target.value)}
                  style={{ margin: "1rem" }}
                  defaultValue={duration}
                  value={groupData.duration} // Ensure the input reflects the current state
                />
                <TextField
                  label={t("Description")}
                  variant='outlined'
                  onChange={(e) => handleOnChange("description", e.target.value)}
                  style={{ margin: "1rem" }}
                  defaultValue={description}
                  value={groupData.description} // Ensure the input reflects the current state
                  multiline
                  rows={4}
                />
                <TextField
                  label={t("Max Participants")}
                  variant='outlined'
                  onChange={(e) => handleOnChange("max_participants", e.target.value)}
                  style={{ margin: "1rem" }}
                  defaultValue={max_participants}
                  value={groupData.max_participants} // Ensure the input reflects the current state
                />
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "spaced-between",
                justifyContent: "space-between",
              }}
            >
              <Button
                variant='outlined'
                size='small'
                onClick={handleOpen}
                className={styles.participants}
                sx={{ padding: "5px 10px", marginTop: "1rem" }} // Adjust padding for a more compact button
              >
                {t("Participants")}: {participants}/{max_participants}
              </Button>

              <Rating name='group-rating' value={rating || 0} precision={1} readOnly />
            </Box>
          </div>
          {editing && <CaptureVideoImage image={capture_image} setImage={handleSetImage} />}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {isVideo() ? (
              <video autoPlay muted loop controls className={styles.image}>
                <source src={groupData.image || capture_image} type='video/mp4' />
                Your browser does not support the video tag.
              </video>
            ) : (
              <img
                src={groupData.image || capture_image}
                alt={`${name}`}
                className={styles.image}
              />
            )}
            {button}
          </div>
        </CardContent>

        <Popover
          open={Boolean(anchorEl) && participants_arr}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          aria-hidden={false}
        >
          <Box padding='16px'>
            <List>
              {participants_arr?.map((participant, index) => (
                <div
                  key={index}
                  className={styles.participant_row}
                  onClick={() => handleProfileRedirect(participant.user_name)}
                >
                  <Avatar src={participant.image} />
                  <ListItem key={participant.user_name} button>
                    <ListItemText primary={participant.user_name} />
                  </ListItem>
                </div>
              ))}
            </List>
          </Box>
        </Popover>
        <Icn
          tooltip={"Chat"}
          color={"lightGreen"}
          onClick={() => openModal(chatRef)}
          label='message'
          icon={<MessageIcon />}
        />
      </Card>
    </T>
  )
}

export default React.memo(Group)
